import { Alert } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { DebtButton } from '@/components/debt/DebtButton';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import type { DebtData } from '@/types';
import { DebtBreakdownTab } from '../_shared/DebtBreakdownTab';

interface AccountDebtBreakdownProps {
  debts: DebtData[];
  totalBalance: number;
}

export function AccountDebtBreakdown({ debts, totalBalance }: AccountDebtBreakdownProps) {
  const { t } = useTranslation();
  const [selectedDebt, setSelectedDebt] = useState<DebtData | null>(null);
  const currency = debts[0]?.currency ?? 'EUR';

  if (selectedDebt) {
    return (
      <div className="relative w-full h-full stack">
        <div className="mx-auto max-w-xl w-full relative">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedDebt(null)}
            aria-label={t('debtBreakdown.allAccounts')}
            className="absolute top-2 left-4 z-10 rounded-full gap-2"
          >
            <ChevronLeft className="size-4" />
          </Button>
        </div>
        <DebtBreakdownTab debt={selectedDebt} />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex-1 stack">
      <div className="w-full sm:max-w-lg mx-auto px-2 xs:px-4 flex-1 h-full space-y-6 overflow-auto">
        <Card className="overflow-hidden pt-5">
          <CardHeader>
            <CardTitle>{t('debtBreakdown.accountsIncluded')}</CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-0 divide-y divide-border-light">
            {debts.map((debt) => (
              <div key={debt.id} className="p-2">
                <DebtButton debt={debt} onClick={setSelectedDebt} variant="compact" />
              </div>
            ))}

            <div className="flex items-center justify-between px-6 pt-4 pb-2">
              <span className="font-semibold">{t('debtBreakdown.totalDebt')}</span>
              <span className="text-lg font-bold">
                <CurrencyDisplay amount={totalBalance} currency={currency} />
              </span>
            </div>
          </CardContent>
        </Card>

        <Alert variant="subtle" className="px-4 py-3 text-sm">
          {t('debtBreakdown.paymentDistributionInfo')}
        </Alert>
      </div>
    </div>
  );
}
